import { useEffect, useRef } from "react";
import type {
  StripePaymentTarget,
  StripeTransactionStatusDto,
} from "types/api/stripe";
import {
  getReturnedStripePaymentIntentId,
  StripePaymentPendingError,
  StripePaymentTargetError,
  StripePaymentTerminalError,
} from "utils/stripe";
import { waitForStripePayment } from "utils/stripePaymentAdapter";

type UseStripePaymentReturnOptions = {
  isStripeReturn: boolean;
  target?: StripePaymentTarget;
  search: string;
  onSuccess?: (transaction: StripeTransactionStatusDto) => void;
  onPending?: () => void;
  onTerminalError?: (error: StripePaymentTerminalError) => void;
  onError?: (error: unknown) => void;
  onInvalidReturn?: () => void;
  onReturnHandled?: () => void;
};

export const useStripePaymentReturn = ({
  isStripeReturn,
  target,
  search,
  onSuccess,
  onPending,
  onTerminalError,
  onError,
  onInvalidReturn,
  onReturnHandled,
}: UseStripePaymentReturnOptions) => {
  const callbacksRef = useRef({
    onSuccess,
    onPending,
    onTerminalError,
    onError,
    onInvalidReturn,
    onReturnHandled,
  });
  callbacksRef.current = {
    onSuccess,
    onPending,
    onTerminalError,
    onError,
    onInvalidReturn,
    onReturnHandled,
  };

  const targetFlow = target?.flow;
  const targetReference =
    target?.flow === "purchase"
      ? String(target.consolidationId)
      : target?.flow === "purchase_all"
        ? target.signId
        : undefined;

  useEffect(() => {
    if (!isStripeReturn) return;

    let isActive = true;
    const paymentIntentId = getReturnedStripePaymentIntentId(search);

    const reconcileReturn = async () => {
      try {
        if (!targetFlow || !paymentIntentId) {
          callbacksRef.current.onInvalidReturn?.();
          return;
        }

        const paymentTarget: StripePaymentTarget =
          targetFlow === "purchase"
            ? { flow: targetFlow, consolidationId: targetReference! }
            : targetFlow === "purchase_all"
              ? { flow: targetFlow, signId: targetReference }
              : { flow: targetFlow };
        const transaction = await waitForStripePayment(
          paymentIntentId,
          paymentTarget,
        );
        if (isActive) callbacksRef.current.onSuccess?.(transaction);
      } catch (error) {
        if (!isActive) return;
        if (error instanceof StripePaymentTargetError) {
          callbacksRef.current.onInvalidReturn?.();
        } else if (error instanceof StripePaymentPendingError) {
          callbacksRef.current.onPending?.();
        } else if (error instanceof StripePaymentTerminalError) {
          callbacksRef.current.onTerminalError?.(error);
        } else {
          callbacksRef.current.onError?.(error);
        }
      } finally {
        if (isActive) callbacksRef.current.onReturnHandled?.();
      }
    };

    void reconcileReturn();
    return () => {
      isActive = false;
    };
  }, [isStripeReturn, search, targetFlow, targetReference]);
};
